import { useCallback, useMemo, useState } from 'react';
import { MOOD_LEVELS } from '@shared/constants';
import { validateMoodRecordDraftComplete } from '@shared/utils/validateMoodRecord';
import { clx } from '@lib/utils';
import { Button } from '@/components/Button';
import { HttpError } from '@/components/ErrorBoundary/types';
import { Modal } from '@/components/Modal/Modal';
import { ConfirmationBalloons } from '@/components/imageSvgs/ConfirmationBalloons';
import { useFormFlowActions } from '@/lib/formFlowEngine';
import { useMoodRecordDraft } from '@/lib/moodUtils/MoodRecordDraftContext';
import { CompleteAndCompletedPageContent } from '../CompleteAndCompletedPageContent';

export const ConfirmationForm = () => {
  const { finish } = useFormFlowActions({ baseUrl: '/mood-log-entry' });
  const { moodRecordDraft, photoFile, removeDraft } = useMoodRecordDraft();
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);

  const handleSaveClick = useCallback(async () => {
    try {
      validateMoodRecordDraftComplete(moodRecordDraft);
      console.log(
        `%cthis is the mood record draft (photo) file type:`,
        'background:#ffd040; color:#222;',
        photoFile?.type
      );
      console.log(`%cthis is the photo file:`, 'background:#bada55; color:#222;', photoFile);
      console.log(
        `%cthis is the mood record draft:`,
        'background:#bada55; color:#222;',
        moodRecordDraft
      );

      try {
        const response = await fetch('/api/mood/save-mood-record', {
          method: 'POST',
          body: JSON.stringify({
            ...moodRecordDraft,
            moodDate: '2025-10-12',
            imageFile: photoFile,
          }),
        }).catch((error) => {
          console.error('Error saving mood record.', error);
          throw new Error('Failed to save mood record. Please try again.');
        });
        const data = await response.json();

        if (data.success) {
          console.log(
            `%cthis is the data:`,
            'color: #221; background: #F40;padding: 2px 8px; font-weight: bold',
            data.message
          );
        } else {
          console.error('Error saving mood record.', data.error);
          throw new Error(data.error || 'Failed to save mood record. Please try again.');
        }

        const debug = true;
        if (debug) {
          return;
        }

        // 3) Cleanup draft and local image.
        try {
          await removeDraft();
        } catch (cleanupError) {
          console.warn('Failed to delete draft or image after save.', cleanupError);
        }

        // 4) Show the confirmation modal.
        setConfirmationModalOpen(true);
      } catch (error) {
        console.error('Save failed.', error);
        if (error instanceof HttpError && error.statusCode === 413) {
          alert(
            'Image size exceeds maximum allowed size (250KB). Please try again with a smaller image.'
          );
        } else {
          alert((error as Error)?.message || 'Failed to save mood record. Please try again.');
        }
      }
    } catch (error) {
      console.error('Validation failed.', error);
      alert('Invalid mood record draft. Please try again.');
    }
  }, [moodRecordDraft, photoFile, removeDraft]);

  const handleDelete = useCallback(async () => {
    // Navigating away from the /mood-log-entry/* pages will trigger the draft removal.
    finish('/');
  }, [finish]);

  const goToCompletedPage = useCallback(() => {
    finish('/mood-log-entry/completed');
  }, [finish]);

  const goToPatchPage = useCallback(() => {
    finish('/patches');
  }, [finish]);

  const shouldOfferPatch = useMemo(() => {
    if (moodRecordDraft?.mood === MOOD_LEVELS.Great) return false;
    if (moodRecordDraft?.mood === MOOD_LEVELS.Good) return false;
    return true;
  }, [moodRecordDraft?.mood]);

  const confirmationModalButtonClasses =
    'flex h-12 basis-32 items-center justify-center p-0 drop-shadow';

  return (
    <>
      <CompleteAndCompletedPageContent
        handleDelete={handleDelete}
        handleSaveClick={handleSaveClick}
        isCompleted={false}
      >
        {/* Confirmation modal */}
        <Modal
          isOpen={confirmationModalOpen}
          onClose={goToCompletedPage}
          hideCloseButton={shouldOfferPatch}
          containerClassName={clx(
            'w-11/12 rounded-2xl drop-shadow',
            'max-w-xs py-0 text-left text-xl font-medium',
            'animate-slide-in-up-fast'
          )}
        >
          <h2 className="mx-auto mt-12 text-xl font-normal">Mood logged. You did it!</h2>

          {shouldOfferPatch ? (
            <div className="my-11 flex justify-center gap-4">
              <Button
                variant="buttonPrimary"
                className={clx(confirmationModalButtonClasses, 'bg-background-primary')}
                onClick={goToPatchPage}
              >
                Patch it up
              </Button>
              <Button
                variant="buttonPrimary"
                className={confirmationModalButtonClasses}
                onClick={goToCompletedPage}
              >
                Not now
              </Button>
            </div>
          ) : (
            <div className="mb-6 mt-3 flex items-center justify-center">
              <ConfirmationBalloons />
            </div>
          )}
        </Modal>
      </CompleteAndCompletedPageContent>
    </>
  );
};
